import { Form } from 'antd';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { useIsFetching, useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { FormFieldsErrors } from '../../../../../common/interfaces';
import { extractFormErrors, messageOnError } from '../../../../../common/utils';
import { Site, SitesQueryKeys } from '../../../../Sites';
import { DomainAutoRegluingsFormFields, DomainAutoRegluingsFormInputs } from '../../../domains.interfaces';
import { useToggleDomainAutoRegluingMutation } from '../../../queries';
import { SiteDomainsAutoRegluingSettingView } from './SiteDomainsAutoRegluingSettingView';
import { domainAutoRegluingsFormValidationSchema } from './validation';

interface Props {
  siteId: string;
  isAutoRegluingsEnabled: Site['isAutoRegluingsEnabled'];
  autoRegluingsVisitor: Site['autoRegluingsVisitor'];
}

export const SiteDomainsAutoRegluingSettingContainer: FC<Props> = memo(({
  siteId,
  isAutoRegluingsEnabled,
  autoRegluingsVisitor
}) => {
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<DomainAutoRegluingsFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof DomainAutoRegluingsFormInputs>>(null);

  const isVisitorSelected = !!Form.useWatch(DomainAutoRegluingsFormInputs.ForVisitor, form) as boolean;

  const { mutate: toggleDomainsAutoRegluingsMutation, isLoading: toggleDomainsAutoRegluingsIsLoading } =
    useToggleDomainAutoRegluingMutation(siteId)({
      onSuccess: async () => {
        await queryClient.refetchQueries([SitesQueryKeys.GetContentById, siteId]);
        await queryClient.refetchQueries([SitesQueryKeys.Dossier, siteId]);
        appMessageApi.success('Налаштування автоматичної переклейки змінено успішно!');
      },
      onError: (err) => {
        form.resetFields([DomainAutoRegluingsFormInputs.IsEnabled]);
        setFormErrors(extractFormErrors<typeof DomainAutoRegluingsFormInputs>(err));
        messageOnError(err, appMessageApi);
      }
    });

  const initialValues: DomainAutoRegluingsFormFields = {
    forVisitor: autoRegluingsVisitor ?? undefined,
    isEnabled: isAutoRegluingsEnabled
  };

  useEffect(() => {
    form.setFieldsValue({
      [DomainAutoRegluingsFormInputs.IsEnabled]: !isAutoRegluingsEnabled,
      [DomainAutoRegluingsFormInputs.ForVisitor]: autoRegluingsVisitor ?? undefined
    });
  }, [isAutoRegluingsEnabled, autoRegluingsVisitor]);

  const handleToggleAutoRegluings = useCallback((): void => {
    setFormErrors(null);

    form.setFieldValue(DomainAutoRegluingsFormInputs.IsEnabled, !isAutoRegluingsEnabled);
    form.submit();
  }, [isAutoRegluingsEnabled, form]);

  const handleManualyValidateForm = useCallback(() => {
    form.validateFields();
  }, []);

  const handleFinish = useCallback((values: DomainAutoRegluingsFormFields) => {
    toggleDomainsAutoRegluingsMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
    form.resetFields([DomainAutoRegluingsFormInputs.IsEnabled]);
  }, []);

  const isSiteDossierLoading = !!useIsFetching([SitesQueryKeys.Dossier, siteId], { exact: true });

  return (
    <SiteDomainsAutoRegluingSettingView
      form={ form }
      formErrors={ formErrors }
      formName="site-domains-auto-regluing-setting-form"
      formType="edit"
      isAutoRegluingsEnabled={ isAutoRegluingsEnabled }
      isVisitorSelected={ isVisitorSelected }
      initialValues={ initialValues }
      isFormLoading={ toggleDomainsAutoRegluingsIsLoading }
      validationSchema={ domainAutoRegluingsFormValidationSchema }
      handleToggleAutoRegluings={ handleToggleAutoRegluings }
      handleManualyValidateForm={ handleManualyValidateForm }
      handleBack={ (): void => {} }
      isButtonsDisabled={ isSiteDossierLoading }
      handleFinish={ handleFinish }
      handleFinishFailed={ handleFinishFailed }
    />
  );
});
